/** @jsxImportSource @emotion/react */
import React from "react";
import * as Icons from "@goodgym/icons";
import * as UI from "@goodgym/components";
import * as T from "@goodgym/graphql/types";
import * as u from "@goodgym/util";
import {
  useJoinWaitingList,
  useLeaveWaitingList,
  useUnSignUpFromSession,
} from "@goodgym/graphql/mutations";
import { useHover } from "usehooks-ts";
import { useAuth } from "@goodgym/auth";

export type SignupButtonProps = UI.ButtonProps & {
  session: T.SessionPageQuery["session"];
  onClick: React.MouseEventHandler;
  easySignup?: boolean;
  refetchSession?: () => Promise<any>;
};

const SignupButton: React.FC<React.PropsWithChildren<SignupButtonProps>> = ({
  session,
  onClick,
  easySignup,
  refetchSession,
  ...rest
}) => {
  const { runner } = useAuth();
  const cancelSignup = useUnSignUpFromSession();
  const joinWaitingList = useJoinWaitingList();
  const leaveWaitingList = useLeaveWaitingList();
  const ref = React.useRef(null);
  const hover = useHover(ref);

  const handleRefetch = () => {
    if (session.type === "CommunityMission") {
      refetchSession?.();
    }
  };

  const TooLate = () => (
    <UI.Button
      variant="contained"
      color="error"
      disabled
      fullWidth
      ref={ref}
      {...rest}
    >
      {session.registerDeadline
        ? "Too late to register"
        : "Session is in the past"}
    </UI.Button>
  );

  const Cancelled = () => (
    <UI.Button
      variant="contained"
      color="error"
      disabled
      fullWidth
      ref={ref}
      {...rest}
    >
      Cancelled
    </UI.Button>
  );

  const AlreadySignedUp = () => {
    const ref = React.useRef(null);
    const hover = useHover(ref);

    const handleClick = (e: React.MouseEvent) => {
      cancelSignup(session)(e);
      setTimeout(handleRefetch, 750);
    };

    return (
      <div ref={ref}>
        <UI.Button
          variant="outlined"
          fullWidth
          color={hover ? "error" : "success"}
          onClick={handleClick}
          {...rest} // safe props only
        >
          {hover ? "Cancel" : "You're going!"}
        </UI.Button>
      </div>
    );
  };

  const JoinWaitingList = () => (
    <UI.Button
      variant={hover ? "contained" : "outlined"}
      color="error"
      onClick={leaveWaitingList(session)}
      fullWidth
      ref={ref}
      {...rest}
    >
      <Icons.Notifications sx={{ mr: 1 }} />
      {hover ? "Leave notification list" : "On notification list"}
    </UI.Button>
  );

  const LeaveWaitingList = () => (
    <UI.Button
      variant="outlined"
      color="error"
      onClick={joinWaitingList(session)}
      fullWidth
      ref={ref}
      {...rest}
    >
      <Icons.Notifications sx={{ mr: 1 }} />
      Join notification list
    </UI.Button>
  );

  const SignIn = () => (
    <UI.SignInButton fullWidth variant="outlined">
      Sign in to register
    </UI.SignInButton>
  );

  const SignUpToSession = () => {
    const isMembersOnly = session?.onlyMember === true;
    const isRunnerMember = runner?.isMember === true;

    const showAsDisabled = isMembersOnly && !isRunnerMember;

    return (
      <UI.Button
        variant="contained"
        color="primary"
        fullWidth
        disabled={showAsDisabled}
        onClick={onClick}
        ref={ref}
        aria-label={showAsDisabled ? "Members Session" : "sign up button"}
        sx={{ fontSize: easySignup ? "28px" : "16px" }}
        {...rest}
      >
        {showAsDisabled
          ? "Members Only"
          : easySignup
          ? "Join us"
          : "Sign up"}
      </UI.Button>
    );
  };


  // Decide which button to show
  if (session.cancelledAt) return <Cancelled />;

  if (session.signups.filter(Boolean).find((s) => s.id === runner?.id))
    return <AlreadySignedUp />;

  if (u.session.isOlderPeopleSession(session)) return null;

  if (
    u.time.isBefore(session.startedAt, session.registerDeadline) ||
    u.time.isAfter(new Date(), session.registerDeadline) ||
    u.time.isAfter(new Date(), session.startedAt)
  )
    return <TooLate />;

  if (!runner && !easySignup) return <SignIn />;

  if (
    session.registerMax &&
    session.registerMax <= session.signups.length &&
    session.onWaitingList
  )
    return <JoinWaitingList />;

  if (
    session.registerMax &&
    session.registerMax <= session.signups.length &&
    !session.onWaitingList
  )
    return <LeaveWaitingList />;

  return <SignUpToSession />;
};

export default SignupButton;
